import React,{
  useEffect,
  useMemo,
  useState
}from'react';

import{
  WHITE,
  BLACK,
  initialChessBoard,
  initialChessState,
  legalChessMoves,
  applyChessMove,
  chessGameState,
  pieceSide
}from'../games/internationalChessEngine';

import{
  chooseInternationalChessAiMove,
  INTERNATIONAL_CHESS_LEVELS
}from'../games/internationalChessAI';

const PIECES={
  wk:'♔',wq:'♕',wr:'♖',
  wb:'♗',wn:'♘',wp:'♙',
  bk:'♚',bq:'♛',br:'♜',
  bb:'♝',bn:'♞',bp:'♟'
};

function playMoveSound(){
  try{
    const Ctx=
      window.AudioContext||
      window.webkitAudioContext;

    if(!Ctx)return;

    const ctx=new Ctx();
    const osc=ctx.createOscillator();
    const gain=ctx.createGain();

    osc.type='sine';
    osc.frequency.value=520;

    gain.gain.setValueAtTime(
      0.06,
      ctx.currentTime
    );

    gain.gain.exponentialRampToValueAtTime(
      0.001,
      ctx.currentTime+0.08
    );

    osc.connect(gain);
    gain.connect(ctx.destination);

    osc.start();
    osc.stop(ctx.currentTime+0.08);

    osc.onended=()=>{
      ctx.close().catch(()=>{});
    };
  }catch(e){}
}

export default function InternationalChess(){
  const[mode,setMode]=useState('ai');
  const[level,setLevel]=useState(5);

  const[board,setBoard]=useState(
    ()=>initialChessBoard()
  );

  const[turn,setTurn]=useState(WHITE);

  const[state,setState]=useState(
    ()=>initialChessState()
  );

  const[selected,setSelected]=useState(null);
  const[history,setHistory]=useState([]);

  const[over,setOver]=useState(false);
  const[result,setResult]=useState('');
  const[busy,setBusy]=useState(false);

  const moves=useMemo(
    ()=>over
      ?[]
      :legalChessMoves(
          board,
          turn,
          state
        ),
    [board,turn,state,over]
  );

  const selectedMoves=useMemo(()=>{
    if(!selected)return[];

    return moves.filter(
      m=>
        m.from[0]===selected[0]&&
        m.from[1]===selected[1]
    );
  },[moves,selected]);

  const checkInfo=useMemo(()=>{
    if(over)return null;

    return chessGameState(
      board,
      turn,
      state
    );
  },[board,turn,state,over]);

  function finishGame(gs){
    setOver(true);
    setBusy(false);

    if(gs.reason==='checkmate'){
      setResult(
        gs.winner===WHITE
          ?'白方将死黑王，白方获胜'
          :'黑方将死白王，黑方获胜'
      );
      return;
    }

    if(gs.reason==='stalemate'){
      setResult('无子可走，和棋');
      return;
    }

    setResult('对局结束');
  }

  function makeMove(move){
    const nextBoard=applyChessMove(
      board,
      move,
      state
    );

    const nextState=
      move.nextState||
      state;

    const nextTurn=
      turn===WHITE
        ?BLACK
        :WHITE;

    setHistory(h=>[
      ...h,
      {
        board,
        turn,
        state,
        over,
        result
      }
    ]);

    setBoard(nextBoard);
    setState(nextState);
    setTurn(nextTurn);
    setSelected(null);

    playMoveSound();

    const gs=chessGameState(
      nextBoard,
      nextTurn,
      nextState
    );

    if(gs.over){
      finishGame(gs);
    }

    return{
      board:nextBoard,
      state:nextState,
      turn:nextTurn,
      gameState:gs
    };
  }

  function handleSquare(r,c){
    if(over||busy)return;

    /*
     * 人机模式玩家固定执白。
     */
    if(
      mode==='ai'&&
      turn!==WHITE
    ){
      return;
    }

    const piece=board[r][c];

    if(selected){
      const move=selectedMoves.find(
        m=>
          m.to[0]===r&&
          m.to[1]===c
      );

      if(move){
        makeMove(move);
        return;
      }
    }

    if(
      piece&&
      pieceSide(piece)===turn
    ){
      setSelected([r,c]);
      return;
    }

    setSelected(null);
  }

  /*
   * AI执黑。
   */
  useEffect(()=>{
    if(
      mode!=='ai'||
      turn!==BLACK||
      over||
      busy
    ){
      return;
    }

    let cancelled=false;

    setBusy(true);

    const timer=setTimeout(()=>{
      try{
        if(cancelled)return;

        const aiMove=
          chooseInternationalChessAiMove(
            board,
            state,
            level
          );

        if(cancelled)return;

        if(!aiMove){
          const gs=chessGameState(
            board,
            BLACK,
            state
          );

          if(gs.over){
            finishGame(gs);
          }else{
            setBusy(false);
          }

          return;
        }

        const nextBoard=applyChessMove(
          board,
          aiMove,
          state
        );

        const nextState=
          aiMove.nextState||
          state;

        setHistory(h=>[
          ...h,
          {
            board,
            turn:BLACK,
            state,
            over:false,
            result:''
          }
        ]);

        setBoard(nextBoard);
        setState(nextState);
        setTurn(WHITE);
        setSelected(null);

        playMoveSound();

        const gs=chessGameState(
          nextBoard,
          WHITE,
          nextState
        );

        if(gs.over){
          finishGame(gs);
        }else{
          setBusy(false);
        }
      }catch(e){
        console.error(
          'International chess AI error',
          e
        );

        setBusy(false);
      }
    },350);

    return()=>{
      cancelled=true;
      clearTimeout(timer);
    };
  },[
    mode,
    turn,
    board,
    state,
    level,
    over
  ]);

  function undo(){
    if(
      busy||
      !history.length
    ){
      return;
    }

    let steps=1;

    /*
     * 人机模式尽量一次撤销
     * “玩家一步 + AI一步”。
     */
    if(
      mode==='ai'&&
      turn===WHITE&&
      history.length>=2
    ){
      steps=2;
    }

    const index=
      history.length-steps;

    const last=history[index];

    setBoard(last.board);
    setTurn(last.turn);
    setState(last.state);
    setOver(last.over);
    setResult(last.result);

    setHistory(
      history.slice(0,index)
    );

    setSelected(null);
    setBusy(false);
  }

  function reset(){
    setBoard(initialChessBoard());
    setTurn(WHITE);
    setState(initialChessState());
    setSelected(null);
    setHistory([]);
    setOver(false);
    setResult('');
    setBusy(false);
  }

  function changeMode(next){
    if(next===mode)return;

    setMode(next);

    setBoard(initialChessBoard());
    setTurn(WHITE);
    setState(initialChessState());
    setSelected(null);
    setHistory([]);
    setOver(false);
    setResult('');
    setBusy(false);
  }

  function resign(){
    if(over||busy)return;

    setOver(true);
    setSelected(null);

    if(mode==='ai'){
      setResult(
        '你已认输，黑方AI获胜'
      );
      return;
    }

    setResult(
      turn===WHITE
        ?'白方认输，黑方获胜'
        :'黑方认输，白方获胜'
    );
  }

  const targetSet=new Set(
    selectedMoves.map(
      m=>`${m.to[0]}-${m.to[1]}`
    )
  );

  return(
    <div style={S.page}>
      <div style={S.wrap}>

        <div style={S.top}>
          <button
            onClick={()=>window.history.back()}
            style={S.back}
          >
            ‹ 返回
          </button>

          <div>
            <div style={S.title}>
              国际象棋
            </div>

            <div style={S.subtitle}>
              {mode==='ai'
                ?'人机对战'
                :'面对面对战'
              }
            </div>
          </div>

          <div style={{width:44}}/>
        </div>

        <div style={S.tabs}>
          <button
            onClick={()=>changeMode('ai')}
            style={{
              ...S.tab,
              ...(mode==='ai'
                ?S.tabOn
                :{})
            }}
          >
            人机对战
          </button>

          <button
            onClick={()=>changeMode('local')}
            style={{
              ...S.tab,
              ...(mode==='local'
                ?S.tabOn
                :{})
            }}
          >
            面对面对战
          </button>
        </div>

        {mode==='ai'&&(
          <div style={S.aiBox}>
            <div style={S.aiTitle}>
              本局对手：
              {level}级 ·
              {' '}
              {INTERNATIONAL_CHESS_LEVELS[level]}
            </div>

            <div style={S.levels}>
              {[1,2,3,4,5,6,7,8,9,10]
                .map(x=>(
                  <button
                    key={x}
                    disabled={
                      busy||
                      history.length>0
                    }
                    onClick={()=>setLevel(x)}
                    style={{
                      ...S.levelBtn,
                      ...(level===x
                        ?S.levelOn
                        :{}),
                      opacity:
                        busy||
                        history.length>0
                          ?.55
                          :1
                    }}
                  >
                    {x}
                  </button>
                ))
              }
            </div>

            <div style={S.aiHint}>
              你执白棋 · AI执黑棋
            </div>
          </div>
        )}

        <div style={S.status}>
          {over?(
            <b>{result}</b>
          ):busy?(
            <b>AI正在思考…</b>
          ):(
            <>
              <b>
                {turn===WHITE
                  ?'白方走棋'
                  :'黑方走棋'
                }
              </b>

              {checkInfo?.check&&(
                <span style={S.check}>
                  将军
                </span>
              )}
            </>
          )}
        </div>

        <div
          style={{
            ...S.board,
            opacity:busy?.88:1
          }}
        >
          {board.map((row,r)=>
            row.map((piece,c)=>{
              const selectedNow=
                selected&&
                selected[0]===r&&
                selected[1]===c;

              const target=
                targetSet.has(
                  `${r}-${c}`
                );

              const dark=
                (r+c)%2===1;

              return(
                <button
                  key={`${r}-${c}`}
                  disabled={busy}
                  onClick={()=>
                    handleSquare(r,c)
                  }
                  style={{
                    ...S.square,
                    background:
                      selectedNow
                        ?'#e8c34f'
                        :dark
                        ?'#9b6b43'
                        :'#f0d9b5'
                  }}
                >
                  {target&&(
                    <span
                      style={
                        piece
                          ?S.capture
                          :S.dot
                      }
                    />
                  )}

                  <span
                    style={{
                      ...S.piece,
                      color:
                        piece?.[0]==='w'
                          ?'#fff'
                          :'#181818',
                      textShadow:
                        piece?.[0]==='w'
                          ?'0 1px 2px #000,0 0 2px #000'
                          :'0 1px 1px #fff5'
                    }}
                  >
                    {piece
                      ?PIECES[piece]
                      :''
                    }
                  </span>
                </button>
              );
            })
          )}
        </div>

        <div style={S.coords}>
          {'abcdefgh'
            .split('')
            .map(x=>(
              <span key={x}>
                {x}
              </span>
            ))
          }
        </div>

        <div style={S.buttons}>
          <button
            onClick={undo}
            disabled={
              busy||
              !history.length
            }
            style={{
              ...S.btn,
              opacity:
                busy||
                !history.length
                  ?.45
                  :1
            }}
          >
            悔棋
          </button>

          <button
            onClick={reset}
            style={S.btn}
          >
            重新开始
          </button>

          <button
            onClick={resign}
            disabled={over||busy}
            style={{
              ...S.btn,
              ...S.resign,
              opacity:
                over||busy
                  ?.45
                  :1
            }}
          >
            认输
          </button>
        </div>

        <div style={S.tip}>
          {mode==='ai'
            ?'你执白棋先行。开始走棋后本局AI等级锁定。'
            :'白方先行，双方在同一设备轮流走棋。'
          }
          <br/>
          支持将军、将死、逼和、王车易位、
          吃过路兵和兵升变。
        </div>

      </div>
    </div>
  );
}

const S={
  page:{
    minHeight:'100vh',
    background:'#f5f1e8',
    padding:'12px 10px 30px',
    boxSizing:'border-box'
  },

  wrap:{
    maxWidth:520,
    margin:'0 auto'
  },

  top:{
    display:'flex',
    alignItems:'center',
    justifyContent:'space-between',
    marginBottom:8
  },

  back:{
    width:44,
    border:0,
    background:'transparent',
    fontSize:16,
    padding:'8px 0',
    textAlign:'left'
  },

  title:{
    textAlign:'center',
    fontSize:23,
    fontWeight:900,
    color:'#3d2b22'
  },

  subtitle:{
    textAlign:'center',
    fontSize:12,
    color:'#8a796c',
    marginTop:2
  },

  tabs:{
    display:'grid',
    gridTemplateColumns:'1fr 1fr',
    gap:8,
    margin:'8px auto 10px',
    width:'min(94vw,500px)'
  },

  tab:{
    border:'1px solid #cdbda9',
    borderRadius:10,
    padding:'9px 4px',
    background:'#fff',
    color:'#675548',
    fontSize:14
  },

  tabOn:{
    background:'#49382f',
    color:'#fff',
    borderColor:'#49382f',
    fontWeight:800
  },

  aiBox:{
    width:'min(94vw,500px)',
    margin:'0 auto 10px',
    padding:'9px 10px',
    boxSizing:'border-box',
    background:'#fff8e8',
    border:'1px solid #d9bd87',
    borderRadius:10,
    textAlign:'center'
  },

  aiTitle:{
    fontSize:14,
    fontWeight:900,
    color:'#49321f'
  },

  levels:{
    display:'grid',
    gridTemplateColumns:'repeat(10,1fr)',
    gap:4,
    marginTop:8
  },

  levelBtn:{
    minWidth:0,
    padding:'6px 0',
    border:'1px solid #d1c0aa',
    borderRadius:6,
    background:'#fff',
    color:'#675548',
    fontSize:12
  },

  levelOn:{
    background:'#9b2c24',
    color:'#fff',
    borderColor:'#9b2c24',
    fontWeight:900
  },

  aiHint:{
    marginTop:6,
    fontSize:11,
    color:'#8a796c'
  },

  status:{
    minHeight:24,
    textAlign:'center',
    margin:'5px 0 8px',
    color:'#49382f',
    fontSize:15
  },

  check:{
    display:'inline-block',
    marginLeft:10,
    color:'#b71c1c',
    fontWeight:900
  },

  board:{
    width:'min(94vw,500px)',
    aspectRatio:'1 / 1',
    margin:'0 auto',
    display:'grid',
    gridTemplateColumns:'repeat(8,1fr)',
    border:'4px solid #59402d',
    boxSizing:'border-box',
    boxShadow:'0 5px 18px #0003'
  },

  square:{
    position:'relative',
    border:0,
    padding:0,
    display:'flex',
    alignItems:'center',
    justifyContent:'center',
    cursor:'pointer',
    overflow:'hidden'
  },

  piece:{
    position:'relative',
    zIndex:2,
    fontSize:'clamp(30px,9vw,52px)',
    lineHeight:1,
    fontFamily:
      '"Times New Roman","DejaVu Sans",serif',
    userSelect:'none'
  },

  dot:{
    position:'absolute',
    zIndex:1,
    width:'24%',
    height:'24%',
    borderRadius:'50%',
    background:'#315c3299'
  },

  capture:{
    position:'absolute',
    zIndex:1,
    width:'78%',
    height:'78%',
    borderRadius:'50%',
    border:'4px solid #8b1f1f99',
    boxSizing:'border-box'
  },

  coords:{
    width:'min(94vw,500px)',
    margin:'5px auto 0',
    display:'grid',
    gridTemplateColumns:'repeat(8,1fr)',
    textAlign:'center',
    color:'#796657',
    fontSize:11
  },

  buttons:{
    width:'min(94vw,500px)',
    margin:'14px auto 0',
    display:'grid',
    gridTemplateColumns:'repeat(3,1fr)',
    gap:10
  },

  btn:{
    border:'1px solid #cbbba7',
    background:'#fff',
    borderRadius:10,
    padding:'10px 5px',
    fontSize:14,
    color:'#49382f'
  },

  resign:{
    color:'#9b2c24'
  },

  tip:{
    width:'min(94vw,500px)',
    margin:'12px auto 0',
    padding:'9px 12px',
    boxSizing:'border-box',
    borderRadius:10,
    background:'#fffaf1',
    color:'#7c6a5b',
    fontSize:12,
    lineHeight:1.7,
    textAlign:'center'
  }
};
